import {type RefObject, useRef, useSyncExternalStore} from 'react'

function arraysEqual(a: unknown[], b: unknown[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index])
}

/**
 * Observe a selector and force a rerender when the list of elements changes. Does not re-render when the descendants
 * of an element change (this avoids infinite render loops when selecting elements to use as containers for new
 * content). Returns the array of matched elements.
 * @param selector The CSS selector to observe.
 * @param elementClass Optional class to filter the elements by. Often `querySelectorAll` is used with a simple type
 * parameter or an assertion because it returns an array of `Element` when it's often clear what the element type will
 * be from the selector. This can be used to safely ensure that all the elements are of that expected type without
 * having to use an assertion. For example, setting to `HTMLInputElement` will ensure that all returned elements are
 * inputs.
 * @param parent Parent element to limit scope of selector and observer. Defaults to `document.body`.
 */
export function useObserveSelector<E extends Element>(
  selector: string,
  elementClass: {new (): E} = Element as {new (): E},
  parentRef?: RefObject<ParentNode | null>,
): E[] {
  const cachedValueRef = useRef<E[] | undefined>(undefined)

  return useSyncExternalStore(
    (onUpdate: () => void) => {
      const observer = new MutationObserver(() => onUpdate())
      observer.observe(parentRef?.current ?? document.body, {subtree: true, childList: true})
      return () => observer.disconnect()
    },
    () => {
      const elements = Array.from((parentRef?.current ?? document.body).querySelectorAll(selector)).filter(
        (e): e is E => e instanceof elementClass,
      )
      if (cachedValueRef.current && arraysEqual(cachedValueRef.current, elements)) return cachedValueRef.current
      return (cachedValueRef.current = elements)
    },
  )
}
